import { ReportsService } from 'src/services/reports/reports.service';
import { AssetPropertyOption, FormDataType, ReportType } from '../types';
import { useAuthentication } from 'src/features/authentication/context';
import useQueryParamsActions from 'src/hooks/useQueryParamsActions';
import { useEffect, useState, useRef } from 'react';
import { useTemplateCache } from '../context/templateCacheContext';
import {
  parsePDFSiteTemplateResponse,
  parsePDFTemplateResponse,
  parseTemplateResponse,
} from '../utils/parseDataTemplateResponse';
import { api } from 'src/api.client';
import {
  ASSET_REPORT_INITIAL_STATE,
  DATA_REPORT_INITIAL_STATE,
  PDF_REPORT_INITAL_STATE,
  PDF_SITE_STATUS_REPORT_INITIAL_STATE,
} from '../config/config';
import { UseFormReturn } from 'react-hook-form';
import assetTypes from '../../asset-details/data/asset_types.json';
import { useLocation } from 'react-router';
import {
  REPORTS_ASSET_PATH,
  REPORTS_DATA_PATH,
  REPORTS_PDF_PATH,
  REPORTS_PDF_SITE_STATUS_PATH,
} from 'src/routes/config';
import { usePDFReportData } from 'src/hooks/usePDFReportData';
import { SitesService } from 'src/services/sites/sites.service';

interface ITemplateQueryProps {
  onTemplateError: (error: Error) => void;
  methods: UseFormReturn<FormDataType, any>;
}

interface ITemplateQueryReturnData {
  data: any;
  loaded: boolean;
  fetched: boolean;
  assetProperties: AssetPropertyOption[];
}

const useTemplateQuery: (props: ITemplateQueryProps) => ITemplateQueryReturnData = ({
  onTemplateError,
}) => {
  const { get } = useQueryParamsActions();
  const location = useLocation();
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const [templateCache, setTemplateCache] = useTemplateCache();
  const reportType = sessionStorage.getItem('report_type');
  const templateId = get('templateId');
  let defaultState;

  const rawTemplateDataRef = useRef<any>(null);

  const { filteredCustomerSites, isPDFDataLoading, assets, customerSites } = usePDFReportData({
    customerId,
    reportType,
  });

  if (reportType === ReportType.ASSET) {
    defaultState = ASSET_REPORT_INITIAL_STATE.data;
  } else if (reportType === ReportType.PDF) {
    defaultState = PDF_REPORT_INITAL_STATE.data;
  } else if (reportType === ReportType.PDF_SITE_STATUS_REPORT) {
    defaultState = PDF_SITE_STATUS_REPORT_INITIAL_STATE.data;
  } else {
    defaultState = DATA_REPORT_INITIAL_STATE.data;
  }

  const [templateData, setTemplateData] = useState<ITemplateQueryReturnData>({
    loaded: false,
    fetched: false,
    assetProperties: [],
    data: {
      state: defaultState,
    },
  });

  useEffect(() => {
    (async () => {
      setTemplateData((prevState) => ({ ...prevState, fetched: false }));

      const getTemplate = async () => {
        let rt = reportType;
        if (!reportType) {
          const url = location.pathname;
          if (url.includes(REPORTS_DATA_PATH)) {
            rt = ReportType.DATA;
          } else if (url.includes(REPORTS_PDF_PATH)) {
            rt = ReportType.PDF;
          } else if (url.includes(REPORTS_ASSET_PATH)) {
            rt = ReportType.ASSET;
          } else if (url.includes(REPORTS_PDF_SITE_STATUS_PATH)) {
            rt = ReportType.PDF_SITE_STATUS_REPORT;
          }
        }

        try {
          const template = await ReportsService.getReportTemplateById(
            rt as string,
            customerId!,
            templateId!
          );
          return template;
        } catch (err) {
          throw err;
        }
      };

      const getSiteTags = async (siteId: string) => {
        if (!customerId) return;
        try {
          const tags = await SitesService.getTags(customerId, siteId);
          return tags;
        } catch (err) {
          throw err;
        }
      };

      const fetchAssetProperties = async () => {
        try {
          const assetPropertiesByAssetType = await Promise.all(
            assetTypes.map(async (asset) => {
              const url = `sites/${customerId}/sites/${siteId}/assets/tags/${asset.value}`;
              try {
                const assetTags = await api.get(url);

                return [
                  asset.value,
                  assetTags.data.map((tag: any) => ({
                    value: tag.id,
                    label: tag.name,
                    available: true,
                  })),
                ];
              } catch (err) {
                throw err;
              }
            })
          );

          return assetPropertiesByAssetType;
        } catch (err) {
          throw err;
        }
      };

      let assetProps: any = [];

      try {
        const url = location.pathname;

        if (reportType === 'ASSET') {
          if (!templateId || !templateCache[templateId]) {
            assetProps = await fetchAssetProperties();
          } else {
            assetProps = templateCache[templateId].assetProperties;
          }
        }

        if (templateId) {
          let temp: any;
          if (templateCache[templateId]) {
            temp = templateCache[templateId].template;
            assetProps = templateCache[templateId].assetProperties;
          } else {
            temp = await getTemplate();

            if (temp.reportType === 'ASSET' && !assetProps.length) {
              assetProps = await fetchAssetProperties();
            }

            sessionStorage.setItem('report_type', temp.reportType);
            setTemplateCache((prevState) => ({
              ...prevState,
              [templateId]: { template: temp, assetProperties: assetProps },
            }));
          }

          rawTemplateDataRef.current = { template: temp };

          if (
            (reportType === 'PDF' || reportType === ReportType.PDF_SITE_STATUS_REPORT) &&
            !isPDFDataLoading &&
            assets?.loaded &&
            filteredCustomerSites
          ) {
            const { timezone, name, ...restTemplate } = temp;
            const templateSiteId = restTemplate?.siteId;
            const sites =
              reportType === ReportType.PDF
                ? filteredCustomerSites || []
                : customerSites.value || [];
            let tags;
            if (!templateSiteId && reportType === ReportType.PDF_SITE_STATUS_REPORT) {
              throw new TypeError('Missing site id from template');
            } else {
              tags = await getSiteTags(templateSiteId);
            }
            const parsedData =
              reportType === 'PDF'
                ? parsePDFTemplateResponse(restTemplate, timezone, sites, assets)
                : parsePDFSiteTemplateResponse(restTemplate, timezone, sites, tags);

            if (parsedData.siteId) {
              setTemplateData((prevState) => ({
                loaded: true,
                fetched: true,
                assetProperties: assetProps,
                data: {
                  ...prevState.data,
                  reportType: temp.reportType,
                  state: parsedData,
                  title: name,
                  timezone,
                },
              }));
            }
          } else if (
            reportType !== ReportType.PDF &&
            reportType !== ReportType.PDF_SITE_STATUS_REPORT &&
            !url.includes(REPORTS_PDF_PATH) &&
            !url.includes(REPORTS_PDF_SITE_STATUS_PATH)
          ) {
            const { timezone, name, ...restTemplate } = temp;
            const parsedData = parseTemplateResponse(restTemplate, timezone, assetProps);

            setTemplateData((prevState) => ({
              loaded: true,
              fetched: true,
              assetProperties: assetProps,
              data: {
                ...prevState.data,
                reportType: temp.reportType,
                state: parsedData,
                title: name,
                timezone,
              },
            }));
          } else {
            setTemplateData((prevState) => ({
              ...prevState,
              fetched: true,
              assetProperties: assetProps,
            }));
          }
        } else {
          setTemplateData((prevState) => ({
            ...prevState,
            loaded: true,
            fetched: true,
            assetProperties: assetProps,
          }));
        }
      } catch (err) {
        onTemplateError(err);
      }
    })();
  }, [
    templateId,
    reportType,
    customerId,
    onTemplateError,
    siteId,
    setTemplateCache,
    templateCache,
    isPDFDataLoading,
    assets,
    filteredCustomerSites,
    location.pathname,
    customerSites,
  ]);

  return templateData;
};

export default useTemplateQuery;
